import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core'
import { CustomField, MediaFile, MediaFileBundle, MediaRootDetail, MediaRootPermission } from 'elements-sdk'
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

import { APIService } from 'api/module'
import { MessageBoxService } from 'common/util/services/messageBox.service'
import { Logger } from 'common/log'
import { BaseComponent, replaceArrayItems } from 'common/helpers'
import { SocketService } from 'common/util/module'
import { ParametersService } from 'main/services/parameters.service'
import { NotifyService } from 'common/util/services/notify.service'
import { PanelService } from 'panel/module'

import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { MediaOperationsService } from 'media.operations'
import { UploaderService } from 'main/services/uploader.service'
import { WorkflowService } from 'media.workflow/services/workflow.service'
import { CustomFieldsService } from 'media.customFields'
import { coerceMediaFile, coerceMediaFileBundle } from 'media/migration'
import { BaseOperation, MediaOperationContext, MediaOperationInput } from 'media.operations/ops/base'
import { MediaLibraryViewOptionsService, MediaLibraryViewStyle } from 'media/services/mediaLibraryViewOptions.service'
import { MediaBrowserSelection } from 'media/services/mediaBrowserSelection.service'
import { isBundle } from 'media/helpers'

export type Item = MediaFile|MediaFileBundle

export interface MediaFileListPagination {
  page: number
  pageSize: number
  total: number
}

@Component({
  selector: 'media-file-list',
  templateUrl: './mediaFileList.component.pug',
  styleUrls: ['./mediaFileList.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaFileListComponent extends BaseComponent {
  @Input() pagination?: MediaFileListPagination
  @Output() paginationChange = new EventEmitter<MediaFileListPagination>()
  @Input() dirs?: MediaFile[]
  @Output() dirsChange = new EventEmitter<MediaFile[]>()
  @Input() bundles: MediaFileBundle[]
  @Output() bundlesChange = new EventEmitter<MediaFileBundle[]>()
  @Input() file?: MediaFile
  @Input() root?: MediaRootDetail
  @Input() showUpload = false
  @Input() showContextMenu = true
  @Input() isSearch = false
  @Input() disableSelection = false
  @Input() goUp?: () => void
  @Output() reloadRequest = new EventEmitter()
  @Output() itemOpened = new EventEmitter<Item>()

  parentFile?: MediaFile
  permission?: MediaRootPermission
  allowDrag = false
  mobileMode = window['mobileMode']
  visibleCustomFields: CustomField[]
  draggingItem?: Item
  opContext: MediaOperationContext
  viewStyle: MediaLibraryViewStyle

  private logger: Logger = new Logger(__filename)
  private lastSelectedItemID?: number
  private visibleCustomFieldsCache = new Map<any, Record<string, any>>()

  importPlural = `Import {count, plural,
    one{1 additional subclip}
    other{{count} additional subclips}}`

  itemsPlural = `{count, plural,
    =0{Empty}
    one{1 item}
    other{{count} items}}`

  constructor (
    private translate: TranslateService,
    private messageBox: MessageBoxService,
    private panel: PanelService,
    private parameters: ParametersService,
    private mediaPermissions: MediaPermissionsService,
    private mediaOperations: MediaOperationsService,
    private api: APIService,
    private workflow: WorkflowService,
    private uploader: UploaderService,
    private customFields: CustomFieldsService,
    private notify: NotifyService,
    private cdr: ChangeDetectorRef,
    private mediaLibraryViewOptions: MediaLibraryViewOptionsService,
    private mediaBrowserSelection: MediaBrowserSelection,
    private router: Router,
    socket: SocketService,
  ) {
    super()
    this.subs.add(socket.subject('cloud:upload-complete').subscribe(data => {
      this.bundles = replaceArrayItems(
        this.bundles,
        b => b.asset?.id === data.asset_id,
        b => ({ ...b, asset: { ...b.asset, hasCloudLinks: true } }),
      )
      this.bundlesChange.emit(this.bundles)
    }))

    this.subs.add(this.mediaBrowserSelection.change$.subscribe(() => {
      cdr.markForCheck()
    }))
  }

  ngOnInit () {
    if (this.file) {
      this.permission = this.mediaPermissions.getPermissionForFile(this.file)!
    }

    this.viewStyle = this.mediaLibraryViewOptions.getViewStyle(this.file, this.root)
    this.subs.add(this.mediaLibraryViewOptions.change$.subscribe(() => {
      this.viewStyle = this.mediaLibraryViewOptions.getViewStyle(this.file, this.root)
      this.cdr.markForCheck()
    }))

    this.allowDrag = !this.mobileMode && !!this.permission?.allowWriteFs

    if (this.root?.customFields) {
      this.visibleCustomFields = this.root.customFields as any
    } else {
      this.visibleCustomFields = []
      this.api.mediaLibrary.getAllCustomFields({}).then(fields => {
        this.visibleCustomFields = fields
      })
    }

    this.opContext = {
      root: this.root,
      location: this.file,
      permission: this.permission,
      isSearch: this.isSearch,
    }

    if (!this.parameters.values.mediaKeepSelectionWhenBrowsing) {
      this.mediaBrowserSelection.clear()
    }
  }

  async runOp (op: BaseOperation, input: MediaOperationInput) {
    await op.run(input, this.opContext)
    if (op.triggersReload) {
      this.reloadRequest.emit()
    }
  }

  toggleSelected (event: MouseEvent, selectedItem: Item): boolean {
    if (event.shiftKey) {
      let items: (Item)[] = this.dirs?.slice() ?? []
      const bundles = this.bundles.filter(x => x.asset)

      items = items.concat(bundles)

      this.mediaBrowserSelection.clear()

      const fromIndex = items.findIndex(el => el.id === this.lastSelectedItemID)
      const toIndex = items.findIndex(el => el.id === selectedItem.id)

      let slicedItems: typeof items = []
      if (fromIndex <= toIndex) {
        slicedItems = items.slice(fromIndex, toIndex + 1)
      } else {
        slicedItems = items.slice(toIndex, fromIndex + 1)
      }

      this.mediaBrowserSelection.toggleSelected(
        slicedItems,
        !this.mediaBrowserSelection.isSelected(selectedItem),
      )
      // this.lastSelectedItemID = selectedItem.id
    } else {
      this.mediaBrowserSelection.toggleSelected(selectedItem)
      this.lastSelectedItemID = selectedItem.id
    }
    return true
  }

  ngOnChanges () {
    if (this.dirs) {
      this.dirs = this.dirs.map(coerceMediaFile)
    }
    if (this.file) {
      this.file = coerceMediaFile(this.file)
      if (this.file.parentFile) {
        this.parentFile = coerceMediaFile(this.file.parentFile)
      }
    }
    this.bundles = this.bundles.map(coerceMediaFileBundle)
  }

  canGoUp () {
    return this.file?.parentFile && this.mediaPermissions.getPermissionForFile(this.file.parentFile) && this.root && this.file.path !== this.root.path
  }

  async open (item: Item, options?: { newTab?: boolean, event?: MouseEvent }) {
    options?.event?.preventDefault()
    options?.event?.stopPropagation()

    if (options?.event?.altKey) {
      return
    }
    if (options?.event?.ctrlKey || options?.event?.metaKey) {
      options.newTab = true
    }

    const url = this.getUrl(item)
    if (url) {
      if (options?.newTab) {
        window.open(url)
      } else {
        this.router.navigateByUrl(url)
      }
      this.itemOpened.emit(item)
    } else {
      this.notify.warning(this.translate.instant('Cannot open this file'))
    }
  }

  getUrl (item: Item) {
    if (!isBundle(item)) {
      return `/media/browse/${this.mediaPermissions.getPermissionForFile(item)!.root}/${item.id}`
    } else if (item.asset) {
      if (item.location) {
        return `/media/view/${this.mediaPermissions.getPermissionForBundle(item)!.root}/${item.location}/${item.asset.id}`
      } else {
        return `/media/view/${this.mediaPermissions.getPermissionForFile(item.mainfile)!.root}/${item.asset.id}`
      }
    }
    return null
  }

  isBundlePresent (bundle) {
    return (!bundle.asset || bundle.asset.hasFiles) && bundle.mainfile?.present
  }

  onItemClick (item: Item, event: MouseEvent) {
    if (this.draggingItem) {
      return
    }
    this.open(item, { event })
  }

  getVisibleCustomFields (item: MediaFile | MediaFileBundle): Record<string, any> {
    if (!this.visibleCustomFieldsCache.has(item)) {
      const values = this.file
        ? this.customFields.combine(
          this.file.effectiveCustomFields,
          isBundle(item) ? item.asset?.customFields : {},
          isBundle(item) ? item.mainfile.customFields : {},
          isBundle(item) ? {} : item.customFields,
        )
        : isBundle(item)
          ? item.mainfile.customFields ?? {}
          : item.effectiveCustomFields ?? item.customFields ?? {}
      const fields = this.visibleCustomFields.filter(f => values[f.name]).slice(0, 10)
      const result = Object.fromEntries(fields.map(f => [f.name, values[f.name]]))
      this.visibleCustomFieldsCache.set(item, result)
    }
    return this.visibleCustomFieldsCache.get(item)!
  }

  onItemDragging (item: Item) {
    const total = this.mediaBrowserSelection.total()
    if (total > 1) {
      const preview = document.querySelector('.cdk-drag-preview')!
      const sublabel = document.createElement('div')
      sublabel.innerText = `${total - 1} more`
      sublabel.className = 'sublabel'
      preview.appendChild(sublabel)
    }
    this.draggingItem = item
  }

  onItemDragStopped (cdkDrag: CdkDrag) {
    setTimeout(() => {
      // let onItemClick still see `draggingItem`
      this.draggingItem = undefined
    }, 100)
    cdkDrag.reset()
  }

  isItemMultidragged (item) {
    return (
      this.draggingItem &&
      this.mediaBrowserSelection.isSelected(item) &&
      (this.draggingItem.id !== item.id || !isBundle(this.draggingItem) !== !isBundle(item)) &&
      this.mediaBrowserSelection.isSelected(this.draggingItem)
    )
  }

  canDropOnto (drag: CdkDrag<Item>, drop: CdkDropList) {
    return drag.data !== drop.data
  }

  async onItemDropped (dropItem, cdk: CdkDragDrop<any>) {
    if (!this.draggingItem) {
      return
    }

    const selection = [
      ...this.dirs?.filter(dir => this.mediaBrowserSelection.isSelected(dir)) ?? [],
      ...this.bundles.filter(bundle => this.mediaBrowserSelection.isSelected(bundle)),
    ]

    if (this.mediaBrowserSelection.isSelected(this.draggingItem)) {
      // if item being dropped is selected, process all selected items
      for (const item of selection) {
        await this.handleItemDropped(item, dropItem, cdk)
      }
    } else {
      await this.handleItemDropped(this.draggingItem, dropItem, cdk)
    }
  }

  async handleItemDropped (dragItem: Item, dropItem: Item, cdk: CdkDragDrop<any>) {
    this.logger.debug('Handling dropping of', dragItem, 'onto', dropItem, cdk)

    const revert = () => cdk.item.reset()

    const resolveItemIntoFiles = async (item) => {
      if (item.isDir) {
        return [item]
      }
      return this.api.mediaLibrary.getAllMediaFiles({ bundle: item.id })
    }

    if (dragItem.id === dropItem.id && !isBundle(dragItem) === !isBundle(dropItem)) {
      revert()
      return
    }

    const dragFiles = await resolveItemIntoFiles(dragItem)
    const dropFiles = await resolveItemIntoFiles(dropItem)

    if (dragFiles.some(x => !x.present)) {
      this.notify.warning(this.translate.instant('Cannot move deleted files'))
      revert()
      return
    }

    try {
      if (dropFiles[0].isDir) {
        // Dropping stuff into a directory
        if (!dropFiles[0].present) {
          this.notify.warning(this.translate.instant('Cannot drop onto deleted folders'))
          revert()
          return
        }

        const dropDirectory = await this.api.mediaLibrary.getMediaFile({
          id: dropFiles[0].id,
          resolveFilePermission: true,
        })

        if (dropDirectory.resolvedPermission && !dropDirectory.resolvedPermission.allowWriteFs) {
          this.notify.warning(this.translate.instant('Cannot drop onto a read-only folder'))
          revert()
          return
        }

        this.logger.debug('Moving', dragFiles, 'into', dropFiles)
        await this.api.storage.moveFiles({
          fileMoveEndpointRequest: {
            input: dragFiles.map(x => x.fullPath),
            destination: dropDirectory.fullPath,
            sync: true,
          },
        })
        this.reloadRequest.emit()
      } else {
        if (!this.file) {
          return
        }
        // Droping one item onto another
        revert()

        if (dropFiles.some(x => !x.present)) {
          this.notify.warning(this.translate.instant('Cannot move deleted files'))
          revert()
          return
        }

        const msg = await this.messageBox.prompt({ prompt: this.translate.instant('New folder name') }).catch(() => null)
        const folderName = msg?.value
        if (!folderName) {
          return
        }
        this.logger.debug('Combining', dragFiles, 'and', dropFiles, 'into', folderName)
        try {
          const newFile = await this.api.storage.createFile({
            fileUpdate: {
              parent: this.file.fullPath,
              name: folderName,
            },
          })
          const newDirectory = await this.api.mediaLibrary.discoverMedia({
            scannerDiscoverEndpointRequest: {
              path: newFile.path,
            },
          })

          const files = [...dragFiles, ...dropFiles]
          await this.api.storage.moveFiles({
            fileMoveEndpointRequest: {
              input: files.map(x => x.fullPath),
              destination: newDirectory.fullPath,
              sync: true,
            },
          })

          this.reloadRequest.emit()
        } catch (e) {
          this.notify.error(this.translate.instant('Could not create the directory'), e.data.message)
        }
      }
    } catch (e) {
      revert()
      throw e
    }
  }
}
